/**
 * Blocker card geometry — shared by the renderer (which draws the card) and the
 * layout (which has to reserve room for it before anything is drawn).
 *
 * A blocker card is the width of a node card, because it sits in a node column
 * and is the same kind of object. It grows downwards instead: wrapped lines
 * make it taller, never wider, and the layout pushes the rest of the tree apart
 * to fit.
 *
 * Each blocker is its own card — a node in the blocker column, positioned by
 * the layout and drawn by its own layer, not furniture hung off a connector.
 * What stops a consequence is usually several separate things, each worth
 * opening and arguing with on its own, so they stack in the column and another
 * one is added the way the first was: by tapping the knot on the edge.
 */

export const BLOCKER_FONT_SIZE = 16;
export const BLOCKER_LINE_HEIGHT = 20;
export const BLOCKER_CARD_PAD_Y = 15;
export const BLOCKER_CARD_PAD_X = 16;
/** Between stacked cards. */
export const BLOCKER_CARD_GAP = 12;


/** Rough advance width of the 16px card text, for wrapping before layout. */
const CHAR_WIDTH = 8.0;

/** How many characters fit on one line inside a card of this width. */
export function blockerWrapChars(cardWidth) {
  const usable = cardWidth - BLOCKER_CARD_PAD_X * 2;
  return Math.max(12, Math.floor(usable / CHAR_WIDTH));
}

/**
 * Greedy word wrap. A word longer than the line is left intact rather than
 * broken — an unreadable blocker defeats the point of showing it.
 */
export function wrapBlockerText(text, maxChars) {
  const words = String(text || '').trim().split(/\s+/);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length <= maxChars || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

/** Wrapped lines per blocker, in card order. */
export function blockerLines(blockers, cardWidth) {
  const maxChars = blockerWrapChars(cardWidth);
  return blockers.map((b) => wrapBlockerText(b.text, maxChars));
}

/** Height of one card, given its wrapped lines. */
export function blockerRowHeight(lines) {
  return Math.max(52, lines.length * BLOCKER_LINE_HEIGHT + BLOCKER_CARD_PAD_Y * 2);
}

/**
 * The stack in the blocker column: one card per blocker, then the add button.
 * Offsets are relative to the stack's centre, which is where the layout parks
 * it — so the renderer and the layout are reading the same arithmetic.
 */
export function blockerStackLayout(blockers, cardWidth) {
  const rows = blockerLines(blockers, cardWidth);
  const heights = rows.map(blockerRowHeight);
  const height = heights.reduce((a, b) => a + b, 0)
    + BLOCKER_CARD_GAP * Math.max(0, heights.length - 1);

  let cursor = -height / 2;
  const cards = rows.map((lines, i) => {
    const cardHeight = heights[i];
    const cy = cursor + cardHeight / 2;
    cursor += cardHeight + BLOCKER_CARD_GAP;
    return { lines, height: cardHeight, cy, blocker: blockers[i] };
  });

  return { cards, height, width: cardWidth };
}

/** Total height the layout has to reserve for one edge's blockers. */
export function blockerCardHeight(blockers, cardWidth) {
  return blockerStackLayout(blockers, cardWidth).height;
}
